import { OpenAICompatibleCompletionLanguageModel, OpenAICompatibleEmbeddingModel, OpenAICompatibleImageModel } from '@ai-sdk/openai-compatible';
import type { EmbeddingModelV3, ImageModelV3, LanguageModelV3, ProviderV3 } from '@ai-sdk/provider';
import type { FetchFunction } from '@ai-sdk/provider-utils';
import { loadApiKey, withoutTrailingSlash, withUserAgentSuffix } from '@ai-sdk/provider-utils';
import { VeniceChatLanguageModel } from './venice-chat-language-model';
import { createVeniceMetadataExtractor } from './venice-metadata-extractor';
import { VERSION } from './version';

export interface VeniceProviderSettings {
    /**
     *Provider name.
     */
    name?: string;

    /**
     * API key for authentication
     */
    apiKey?: string;

    /**
     * Base URL for API calls
     */
    baseURL?: string;

    /**
     * Custom headers for requests
     */
    headers?: Record<string, string>;

    /**
     *Optional custom url query parameters to include in request urls.
     */
    queryParams?: Record<string, string>;

    /**
     * Custom fetch implementation
     */
    fetch?: FetchFunction;

    /**
     * Include usage information in streaming responses.
     */
    includeUsage?: boolean;

    /**
     * Whether the provider supports structured outputs in chat models.
     */
    supportsStructuredOutputs?: boolean;
}

export interface VeniceProvider extends ProviderV3 {
    (modelId: string): VeniceChatLanguageModel;
    languageModel(modelId: string): VeniceChatLanguageModel;
    chatModel(modelId: string): VeniceChatLanguageModel;
    completionModel(modelId: string): LanguageModelV3;
    imageModel(modelId: string): ImageModelV3;
    embeddingModel(modelId: string): EmbeddingModelV3;

    /** @deprecated Use `embeddingModel` instead. */
    textEmbeddingModel(modelId: string): EmbeddingModelV3;
}

export function createVenice(options: VeniceProviderSettings = {}): VeniceProvider {
    const baseURL = withoutTrailingSlash(options.baseURL ?? 'https://api.venice.ai/api/v1') as string;
    const providerName = options.name ?? 'venice';

    const getHeaders = () =>
        withUserAgentSuffix(
            {
                Authorization: `Bearer ${loadApiKey({
                    apiKey: options.apiKey,
                    environmentVariableName: 'VENICE_API_KEY',
                    description: 'Venice Provider API key',
                })}`,
                ...options.headers,
            },
            `ai-sdk/venice/${VERSION}`
        );

    const getModelConfig = (modelType: string) => ({
        provider: `${providerName}.${modelType}`,
        url: ({ path }: { path: string }) => {
            const url = new URL(`${baseURL}${path}`);
            if (options.queryParams) {
                url.search = new URLSearchParams(options.queryParams).toString();
            }
            return url.toString();
        },
        headers: getHeaders,
        fetch: options.fetch,
    });

    const createChatModel = (modelId: string) =>
        new VeniceChatLanguageModel(modelId, {
            ...getModelConfig('chat'),
            includeUsage: options.includeUsage,
            supportsStructuredOutputs: options.supportsStructuredOutputs,
            metadataExtractor: createVeniceMetadataExtractor(providerName),
        });

    const createLanguageModel = (modelId: string) => createChatModel(modelId);

    const provider = (modelId: string) => createLanguageModel(modelId);
    provider.specificationVersion = 'v3' as const;
    provider.chatModel = createChatModel;
    provider.languageModel = createLanguageModel;

    provider.completionModel = (modelId: string) => new OpenAICompatibleCompletionLanguageModel(modelId, { ...getModelConfig('completion'), includeUsage: options.includeUsage });
    provider.imageModel = (modelId: string) => new OpenAICompatibleImageModel(modelId, getModelConfig('image'));
    provider.embeddingModel = (modelId: string) => new OpenAICompatibleEmbeddingModel(modelId, getModelConfig('embedding'));
    provider.textEmbeddingModel = (modelId: string) => new OpenAICompatibleEmbeddingModel(modelId, getModelConfig('embedding'));

    return provider as VeniceProvider;
}

export const venice = createVenice();
